import React, { useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Sparkles,
  Shield,
  CreditCard,
  ArrowRight,
  Loader2,
  TrendingUp,
} from "lucide-react";
import PhoneAnchor from "./PhoneAnchor";
import { SERVICE_GRID } from "./StageServices";

const KEY_TO_DEMO_ID = {
  private_chef: "private-chef",
  massage: "massage",
  airport_ride: "airport-pickup",
  arrival_event: "arrival-event",
  wellness_therapy: "wellness-therapy",
  restaurant_reservations: "restaurant-reservations",
  other: "other",
};

export default function StagePricing({
  previewToggles,
  onToggle,
  pricing,
  onSubscribe,
  actionLoading,
  hostLogo,
}) {
  const enabledCount = Object.values(previewToggles || {}).filter(Boolean).length;
  const propertyCount = Math.max(Number(pricing?.propertyCount) || 0, 0);

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(previewToggles || {}).forEach(([k, on]) => {
      if (on) {
        const id = KEY_TO_DEMO_ID[k];
        if (id) set.add(id);
      }
    });
    return set;
  }, [previewToggles]);

  return (
    <div className="relative w-full h-full grid grid-rows-[minmax(0,35%)_minmax(0,65%)] laptop:grid-rows-1 laptop:grid-cols-[1.3fr_0.7fr]">
      {/* Left — calculator */}
      <div className="relative px-6 laptop:px-14 py-3 laptop:py-6 order-2 laptop:order-1 min-h-0 flex flex-col overflow-y-auto laptop:overflow-hidden">
        <div className="mb-3 laptop:mb-5">
          <p className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888] mb-1.5">
            Step 3 · Pricing
          </p>
          <h2 className="text-[22px] laptop:text-[28px] font-extrabold text-[#111111] leading-tight" style={{ letterSpacing: -0.4 }}>
            Pricing based on your property count.
          </h2>
        </div>

        {/* Chips row — compact */}
        <div className="laptop:flex-1 laptop:min-h-0 laptop:overflow-y-auto laptop:pr-1">
          <div className="grid grid-cols-2 tabletOnly:grid-cols-3 laptop:grid-cols-3 desktop:grid-cols-4 gap-2">
            {SERVICE_GRID.map((svc) => {
              const on = !!previewToggles?.[svc.key];
              const Icon = svc.icon;
              return (
                <motion.button
                  key={svc.key}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => onToggle(svc.key, !on)}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-[10px] border transition-all text-left ${
                    on
                      ? "bg-white border-[#FECACA] shadow-[0_6px_20px_rgba(0,0,0,0.04)]"
                      : "bg-[#F7F7F8] border-[#EAEAEA] hover:bg-white"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-[8px] flex-shrink-0 flex items-center justify-center ${
                      on ? "bg-[#EF4949] text-white" : "bg-white border border-[#EAEAEA] text-[#888888]"
                    }`}
                  >
                    <Icon size={13} strokeWidth={2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="block text-[12px] font-bold text-[#111111] truncate">{svc.name}</span>
                    <span className="block text-[10px] font-semibold text-[#10B981]">
                      {on ? "Selected" : "Tap to include"}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Calculator strip */}
        <div className="mt-4 laptop:mt-6 rounded-[16px] bg-white border border-[#EAEAEA] shadow-[0_18px_60px_rgba(0,0,0,0.05)] p-4 laptop:p-5">
          <div className="flex items-center justify-between gap-4 flex-wrap laptop:flex-nowrap">
            <div className="flex items-center gap-4 laptop:gap-6 flex-1 min-w-0">
              <Calc label="Services" value={enabledCount} />
              <Divider />
              <Calc label="Properties" value={propertyCount} icon={<Home size={11} />} />
              <Divider />
              <Calc label="Billing" value={pricing.isQuote ? "Custom" : "Monthly"} />
            </div>
            <div className="flex items-end gap-1 flex-shrink-0">
              <AnimatePresence mode="wait">
                <motion.div
                  key={pricing.displayPrice}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  {pricing.isQuote ? (
                    <span
                      className="text-[24px] laptop:text-[30px] font-extrabold text-[#111111] leading-none"
                      style={{ letterSpacing: -0.6 }}
                    >
                      {pricing.displayPrice}
                    </span>
                  ) : (
                    <span
                      className="text-[32px] laptop:text-[40px] font-extrabold text-[#111111] leading-none tabular-nums"
                      style={{ letterSpacing: -0.8 }}
                    >
                      {pricing.displayPrice}
                    </span>
                  )}
                </motion.div>
              </AnimatePresence>
              {!pricing.isQuote && (
                <span className="text-[12px] font-semibold text-[#888888] pb-1">/mo</span>
              )}
            </div>
          </div>

          <p className="mt-2 text-[11px] text-[#5F6368]">{pricing.helperText}</p>

          <div className="mt-3 pt-3 border-t border-[#EAEAEA] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] text-[#5F6368]">
              <TrendingUp size={12} className="text-[#10B981]" />
              Early/late check-in/out can more than cover the monthly cost
            </div>
            <button
              onClick={() => onSubscribe("stage_pricing")}
              disabled={actionLoading || enabledCount === 0}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-[12px] text-[13px] font-bold transition-colors ${
                enabledCount === 0
                  ? "bg-[#EAEAEA] text-[#888888] cursor-not-allowed"
                  : "bg-[#EF4949] text-white hover:bg-[#DC2626] shadow-[0_8px_32px_rgba(239,73,73,0.12)]"
              }`}
            >
              {actionLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : enabledCount === 0 ? (
                "Select at least one service"
              ) : (
                <>
                  {pricing.ctaLabel}
                  <ArrowRight size={13} strokeWidth={2.5} />
                </>
              )}
            </button>
          </div>

          <div className="mt-2.5 flex items-center gap-3 text-[10px] text-[#888888]">
            <span className="flex items-center gap-1"><Shield size={10} /> {pricing.isQuote ? "Custom onboarding" : "Cancel anytime"}</span>
            <span className="flex items-center gap-1"><CreditCard size={10} /> {pricing.isQuote ? "Team follow-up by email" : "Secure checkout"}</span>
            <span className="hidden laptop:flex items-center gap-1"><Sparkles size={10} className="text-[#EF4949]" /> Grocery stays free</span>
          </div>
        </div>
      </div>

      {/* Right — phone */}
      <div className="relative order-1 laptop:order-2 min-h-0 overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(500px 400px at 55% 45%, rgba(17,17,17,0.05), transparent 60%)",
          }}
        />
        <div className="relative h-full flex items-center justify-center py-2 laptop:py-6">
          <PhoneAnchor stage={3} enabledServices={enabledServices} hostLogo={hostLogo} />
        </div>
      </div>
    </div>
  );
}

function Calc({ label, value, icon }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-[#888888]">
        {icon}
        {label}
      </div>
      <div className="text-[18px] laptop:text-[22px] font-extrabold text-[#111111] leading-tight tabular-nums mt-0.5" style={{ letterSpacing: -0.3 }}>
        {value}
      </div>
    </div>
  );
}

function Divider() {
  return <div className="h-8 w-px bg-[#EAEAEA]" />;
}
